import { z } from 'zod'
import type { ApiClient } from '../apiClient'

export const dimCatalogRowSchema = z.object({
  fact_table: z.string(),
  dim_position: z.string(),
  dim_name: z.string(),
})
export const dimCatalogSchema = z.array(dimCatalogRowSchema)
export type DimCatalogRow = z.infer<typeof dimCatalogRowSchema>

export const valueLabelRowSchema = z.object({
  fact_table: z.string(),
  dim_name: z.string(),
  value: z.string(),
  display_label: z.string(),
  is_default: z.boolean().nullable().optional(),
  is_stackable: z.boolean().nullable().optional(),
  is_additive: z.boolean().nullable().optional(),
  parent_kpi: z.string().nullable().optional(),
  page: z.string().nullable().optional(),
})
export const valueLabelsSchema = z.array(valueLabelRowSchema)
export type ValueLabelRow = z.infer<typeof valueLabelRowSchema>

export async function getDimCatalog(client: ApiClient): Promise<DimCatalogRow[]> {
  const raw = await client.get<unknown>('/v_dim_catalog')
  return dimCatalogSchema.parse(raw)
}

export async function getValueLabels(
  client: ApiClient,
  factTable: string,
): Promise<ValueLabelRow[]> {
  const raw = await client.get<unknown>('/v_value_labels', { fact_table: `eq.${factTable}` })
  return valueLabelsSchema.parse(raw)
}

export async function getLatestRevision(client: ApiClient): Promise<number> {
  const raw = await client.get<Array<{ revision_id: number }>>('/v_latest_data_update_rev')
  if (raw.length === 0) {
    // Internal-contract violation: the view aggregates over fact tables and should
    // always have at least one row. Loud failure beats a silent null that disables
    // staleness detection (Batch F audit #7).
    throw new Error('v_latest_data_update_rev returned no rows — view broken or schema regressed')
  }
  return raw[0]!.revision_id
}

export const kpiDimStackableRowSchema = z.object({
  kpi: z.string(),
  dim_name: z.string(),
  is_stackable: z.boolean().nullable().optional(),
})
export const kpiDimStackableSchema = z.array(kpiDimStackableRowSchema)
export type KpiDimStackableRow = z.infer<typeof kpiDimStackableRowSchema>

export async function getKpiDimStackable(
  client: ApiClient,
  factTable: string,
): Promise<KpiDimStackableRow[]> {
  const raw = await client.get<unknown>('/v_kpi_dim_stackable', { fact_table: `eq.${factTable}` })
  return kpiDimStackableSchema.parse(raw)
}
